/**
 * Regras PURAS da venda assistida (ADR-001 E5): validacao do formulario,
 * dinheiro digitado -> centavos, janela de 7 dias do ultimo toque, texto do
 * desfecho e traducao dos erros HTTP. Usado pelo Comercial e pelo detalhe do
 * lead -- as duas telas registram a venda pelo MESMO caminho e dizem a mesma
 * coisa sobre ela.
 *
 * Decisoes que fogem do obvio:
 *  1. Dinheiro digitado vira centavos por TEXTO (reais x 100 + centavos),
 *     nunca por `Number(texto) * 100`: 0,29 * 100 = 28,999999999999996.
 *  2. "497.00" (ponto como decimal, habito de quem digita no celular) vale
 *     R$ 497,00 e nao R$ 49.700,00. O ponto so e milhar quando separa grupos
 *     de exatamente 3 digitos ("1.234" ou "1.234,56"). Errar isso cobra 100x.
 *  3. A validacao espelha o servidor (valor > 0, referencia
 *     `[A-Za-z0-9._:-]{1,128}`, pagamento nao no futuro) para o erro aparecer
 *     no campo, antes da rede; o servidor continua sendo quem decide.
 *  4. A janela usa a mesma folga de relogio de 2 min do backend, e SO na
 *     mesma ponta: um toque registrado "1 min depois" do pagamento ainda
 *     conta; um toque de 7 dias e 1 min antes do pagamento nao conta
 *     (`pickCreditedContact`: earliest = pago - 7 dias, sem folga). Folga nas
 *     duas pontas faria a previa prometer "creditado" onde o motor nega.
 *  5. O desfecho nunca credita a vendedora: ela e "fechado por", o credito e
 *     do ultimo toque do motor (decisao do dono, 25/09/2026).
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { formatBRLFromCents } from '../../lib/format';
import type { RefundPolicy } from '../../types';
import type { DesfechoDaVendaAssistida, ToqueDoMotor, VendaAssistidaInput, VendaAssistidaResposta } from './types';

export const REFERENCIA_DO_PEDIDO = /^[A-Za-z0-9._:-]{1,128}$/;
export const LIMITE_DA_REFERENCIA = 128;
export const LIMITE_DA_NOTA = 500;
export const JANELA_DE_COMISSAO_DIAS = 7;
export const JANELA_DE_COMISSAO_MS = JANELA_DE_COMISSAO_DIAS * 86_400_000;
export const FOLGA_DE_RELOGIO_MS = 2 * 60_000;

// -----------------------------------------------------------------------------
// Dinheiro digitado
// -----------------------------------------------------------------------------

/** "1.234,5" -> 123450; "497.00" -> 49700; "897" -> 89700; texto invalido -> null. */
export function parseReaisParaCentavos(texto: string): number | null {
  let limpo = texto.replace(/R\$/u, '').replace(/\s/gu, '');
  if (limpo === '') {
    return null;
  }
  if (limpo.includes(',')) {
    limpo = limpo.replace(/\./g, '');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) {
    limpo = limpo.replace(/\./g, '');
  } else if (/^\d+\.\d{1,2}$/.test(limpo)) {
    limpo = limpo.replace('.', ',');
  }
  if (!/^\d+(,\d{0,2})?$/.test(limpo)) {
    return null;
  }
  const [reais = '0', centavos = ''] = limpo.split(',');
  const reaisNumero = Number(reais);
  const centavosNumero = Number(centavos.padEnd(2, '0'));
  if (!Number.isSafeInteger(reaisNumero) || !Number.isSafeInteger(centavosNumero)) {
    return null;
  }
  const total = reaisNumero * 100 + centavosNumero;
  return Number.isSafeInteger(total) ? total : null;
}

/** Centavos -> "897,00" para preencher o campo (sem o "R$", que ja esta no rotulo). */
export function centavosParaCampo(cents: number): string {
  return formatBRLFromCents(cents).replace(/^R\$\s*/u, '');
}

// -----------------------------------------------------------------------------
// Data do pagamento (input datetime-local, no fuso do navegador)
// -----------------------------------------------------------------------------

function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

/** Date -> "2026-09-24T09:00" no fuso local, formato do `<input type="datetime-local">`. */
export function dataParaCampo(data: Date): string {
  return `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}T${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`;
}

/** Valor do campo -> ISO-8601; vazio ou invalido -> null. */
export function campoParaIso(valor: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(valor)) {
    return null;
  }
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data.toISOString();
}

// -----------------------------------------------------------------------------
// Validacao
// -----------------------------------------------------------------------------

export interface ErrosDaVenda {
  readonly valor: string | null;
  readonly referencia: string | null;
  readonly pagoEm: string | null;
  readonly nota: string | null;
}

export function validarVendaAssistida(dados: VendaAssistidaInput, agora: Date): ErrosDaVenda {
  const valor = !Number.isSafeInteger(dados.amountCents) ? 'Digite um valor em reais, como 497,00.' : dados.amountCents <= 0 ? 'O valor pago precisa ser maior que zero.' : null;
  const ref = dados.orderRef;
  const referencia =
    ref.length === 0
      ? 'Informe a referência do pedido (id do Pix, do pedido ou da nota).'
      : ref.length > LIMITE_DA_REFERENCIA
        ? `A referência tem ${ref.length} caracteres; o limite é ${LIMITE_DA_REFERENCIA}.`
        : REFERENCIA_DO_PEDIDO.test(ref)
          ? null
          : 'Use só letras sem acento, números e . _ : - (sem espaços).';
  let pagoEm: string | null = null;
  if (dados.paidAt !== undefined) {
    const t = new Date(dados.paidAt).getTime();
    if (Number.isNaN(t)) {
      pagoEm = 'Data do pagamento inválida.';
    } else if (t > agora.getTime() + FOLGA_DE_RELOGIO_MS) {
      pagoEm = 'O pagamento não pode estar no futuro.';
    }
  }
  const nota = dados.note !== undefined && dados.note.length > LIMITE_DA_NOTA ? `A observação passou de ${LIMITE_DA_NOTA} caracteres.` : null;
  return { valor, referencia, pagoEm, nota };
}

export function temErro(erros: ErrosDaVenda): boolean {
  return erros.valor !== null || erros.referencia !== null || erros.pagoEm !== null || erros.nota !== null;
}

export function primeiroErro(erros: ErrosDaVenda): string | null {
  return erros.valor ?? erros.referencia ?? erros.pagoEm ?? erros.nota;
}

// -----------------------------------------------------------------------------
// Janela de 7 dias (ADR-001)
// -----------------------------------------------------------------------------

export interface JanelaDoToque {
  readonly terminaEm: Date;
  /** O pagamento informado cai dentro da janela (com a folga de relogio). */
  readonly pagamentoDentro: boolean;
  /** Minutos que ainda restam a partir de `agora` (negativo = vencida). */
  readonly restanteMin: number;
}

export function janelaDoToque(toque: ToqueDoMotor | null, pagoEm: Date, agora: Date): JanelaDoToque | null {
  if (toque === null) {
    return null;
  }
  const inicio = new Date(toque.at).getTime();
  if (Number.isNaN(inicio)) {
    return null;
  }
  const fim = inicio + JANELA_DE_COMISSAO_MS;
  const pago = pagoEm.getTime();
  return {
    terminaEm: new Date(fim),
    pagamentoDentro: inicio <= pago + FOLGA_DE_RELOGIO_MS && pago - inicio <= JANELA_DE_COMISSAO_MS,
    restanteMin: Math.round((fim - agora.getTime()) / 60_000),
  };
}

/** "3 d 4 h", "5 h", "12 min". Para prazos, nao para duracao de ligacao. */
export function prazoCurto(minutos: number): string {
  const m = Math.max(0, Math.round(minutos));
  if (m < 60) {
    return `${m} min`;
  }
  if (m < 1_440) {
    return `${Math.floor(m / 60)} h`;
  }
  const dias = Math.floor(m / 1_440);
  const horas = Math.floor((m % 1_440) / 60);
  return horas === 0 ? `${dias} d` : `${dias} d ${horas} h`;
}

// -----------------------------------------------------------------------------
// Desfecho em linguagem de negocio
// -----------------------------------------------------------------------------

export interface DesfechoDescrito {
  readonly tom: BadgeTone;
  readonly icone: IconName;
  /** Frase curta: vira titulo do painel de resultado e o aviso da lista. */
  readonly titulo: string;
  readonly texto: string;
  /** Selo curto para listas ("Comissão creditada", "Sem comissão"). */
  readonly selo: string;
  /** Houve gravacao nova (tudo menos `duplicado`). */
  readonly gravou: boolean;
}

const CANAL_DO_CREDITO: Readonly<Record<'voice' | 'whatsapp' | 'email', string>> = {
  voice: 'Ligação da IA',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
};

/** Artigo do contato: "a" Ligacao da IA, "o" WhatsApp, "o" E-mail, "o" ultimo toque. */
export function artigoDoContato(rotulo: string): 'à' | 'ao' {
  return /^liga/iu.test(rotulo.trim()) ? 'à' : 'ao';
}

/** "ao WhatsApp T+1h", "à Ligação da IA T+30min" -- "creditada ao Ligação" e erro que o lojista le. */
export function aoContato(rotulo: string): string {
  return `${artigoDoContato(rotulo)} ${rotulo}`;
}

/** Rotulo do contato creditado: o do servidor, se vier; senao o toque que a tela conhece. */
export function rotuloDoCredito(resposta: VendaAssistidaResposta, toque: ToqueDoMotor | null): string | null {
  const doServidor = resposta.creditedTo;
  if (doServidor !== undefined && doServidor !== null) {
    return `${CANAL_DO_CREDITO[doServidor.channel]} ${doServidor.touchLabel}`.trim();
  }
  return toque?.rotulo ?? null;
}

export function descreverDesfecho(
  resposta: VendaAssistidaResposta,
  contexto: { readonly toque: ToqueDoMotor | null; readonly amountCents: number; readonly orderRef: string; readonly fechadoPor: string | null },
): DesfechoDescrito {
  const credito = rotuloDoCredito(resposta, contexto.toque);
  const alvo = credito === null ? 'último toque da régua' : credito;
  const valor = formatBRLFromCents(contexto.amountCents);
  const quem = contexto.fechadoPor === null ? '' : ` ${contexto.fechadoPor} fica como “fechado por”.`;
  const comissao = resposta.commissionCents === null ? 'calculada pelo motor' : formatBRLFromCents(resposta.commissionCents);
  const desfecho: DesfechoDaVendaAssistida = resposta.conversion;
  switch (desfecho) {
    case 'creditado':
      return {
        tom: 'good',
        icone: 'check',
        titulo: `Comissão devida: creditada ${aoContato(alvo)}`,
        texto: `Venda de ${valor} registrada (pedido ${contexto.orderRef}). Comissão do Converte Fácil: ${comissao}, congelada com a taxa de hoje.${quem}`,
        selo: 'Comissão creditada',
        gravou: true,
      };
    case 'creditado_revertido':
      return {
        tom: 'warning',
        icone: 'alerta',
        titulo: `Creditada ${aoContato(alvo)} e já revertida`,
        texto: `Já havia um estorno para o pedido ${contexto.orderRef} e a política da empresa desfaz esse motivo. A venda de ${valor} fica no histórico com a comissão revertida.${quem}`,
        selo: 'Comissão revertida',
        gravou: true,
      };
    case 'sem_contato':
      return {
        tom: 'neutral',
        icone: 'info',
        titulo: `Sem toque da régua nos últimos ${JANELA_DE_COMISSAO_DIAS} dias: sem comissão`,
        texto: `A venda de ${valor} ficou registrada (pedido ${contexto.orderRef}) e entra no histórico da empresa, mas não gera comissão: a régua não tocou este lead nos ${JANELA_DE_COMISSAO_DIAS} dias antes do pagamento (ADR-001).${quem}`,
        selo: 'Sem comissão',
        gravou: true,
      };
    case 'duplicado':
      return {
        tom: 'info',
        icone: 'copiar',
        titulo: 'Esta venda já estava registrada',
        texto: `O pedido ${contexto.orderRef} já tinha entrado antes. Nada foi gravado de novo e nada será cobrado em dobro.`,
        selo: 'Já registrada',
        gravou: false,
      };
    default: {
      const nunca: never = desfecho;
      return nunca;
    }
  }
}

// -----------------------------------------------------------------------------
// Politica de estorno (ADR-001 E2) -- por empresa, nunca um texto fixo
// -----------------------------------------------------------------------------

/** Padrao do schema (`companies.refund_policy`) quando a empresa nao escolheu. */
export const POLITICA_DE_ESTORNO_PADRAO: RefundPolicy = 'reverte_chargeback';

/**
 * O que acontece com a comissao desta venda num estorno, pela politica DA
 * EMPRESA. Texto fixo "reembolso nao reverte" mentiria para quem escolheu
 * `reverte_tudo` (e para quem escolheu `nao_reverte`, sobre o chargeback).
 * Reembolso parcial nunca reverte, em politica nenhuma.
 */
export function textoDaPoliticaDeEstorno(politica: RefundPolicy): string {
  switch (politica) {
    case 'nao_reverte':
      return 'Pela política de estorno desta empresa, nem reembolso nem chargeback revertem a comissão.';
    case 'reverte_chargeback':
      return 'Pela política de estorno desta empresa, reembolso não reverte a comissão; chargeback reverte.';
    case 'reverte_tudo':
      return 'Pela política de estorno desta empresa, reembolso total e chargeback revertem a comissão; reembolso parcial não reverte.';
    default: {
      const nunca: never = politica;
      return nunca;
    }
  }
}

// -----------------------------------------------------------------------------
// Erros
// -----------------------------------------------------------------------------

const REENVIO_SEGURO = 'Se a venda chegou a entrar, reenviar com a mesma referência não duplica: o motor responde que ela já foi registrada.';

/** Traduz a falha do POST em uma frase que diz o que fazer. */
export function mensagemDeErroDaVenda(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return `Falha inesperada ao registrar a venda. ${REENVIO_SEGURO}`;
  }
  switch (error.status) {
    case 400:
      switch (error.code) {
        case 'valor_invalido':
          return 'O servidor recusou o valor: ele precisa ser maior que zero, em reais e centavos.';
        case 'referencia_invalida':
          return 'O servidor recusou a referência: use só letras sem acento, números e . _ : - (até 128).';
        case 'pago_no_futuro':
        case 'data_invalida':
          return 'O servidor recusou a data do pagamento: ela não pode estar no futuro.';
        case 'dados_invalidos':
          return error.message;
        default:
          return 'O servidor recusou os dados da venda. Confira valor, referência e data do pagamento.';
      }
    case 401:
      return 'Sua sessão expirou. Entre de novo; a venda não foi registrada.';
    case 403:
      return error.code === 'cabecalho_xhr_ausente'
        ? 'O navegador não enviou a proteção contra envio forjado. Recarregue a página e tente de novo.'
        : 'Seu usuário não pode registrar vendas desta empresa. Peça ao dono da conta ou à equipe do Converte Fácil.';
    case 404:
      return error.code === 'lead_nao_encontrado'
        ? 'Este lead não existe nesta empresa: pode ter sido apagado ou pertencer a outra empresa.'
        : 'O registro de venda assistida ainda não está disponível neste servidor, ou o lead não pertence a esta empresa.';
    case 409:
      return `Esta referência já foi usada em outra venda da empresa. Confira o pedido. ${REENVIO_SEGURO}`;
    case 429:
      return error.userMessage;
    default:
      if (error.code === 'resposta_invalida') {
        return `O servidor respondeu num formato inesperado. ${REENVIO_SEGURO}`;
      }
      return `${error.userMessage} ${REENVIO_SEGURO}`;
  }
}
